<template>
  <div class="app-stats-report">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">{{ reportTitle }}</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ reportItems.length }} Records
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading app stats...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" @click="loadReportData" class="mt-3">
        Retry
      </b-button>
    </div>

    <div v-else class="report-page">
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div class="logo-wrapper mr-3" v-if="schoolLogo">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">{{ reportTitle }}</span>
              <span class="date-range-text">
                <strong>Generated on {{ currentDateTime }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="summary-section mb-4">
        <div class="summary-card">
          <span class="label">User Type</span>
          <span class="value text-primary">{{ userTypeLabel }}</span>
        </div>
        <div class="summary-card">
          <span class="label">Download Status</span>
          <span
            class="value"
            :class="status === 'active' ? 'text-success' : 'text-danger'"
          >
            {{ statusLabel }}
          </span>
        </div>
        <div class="summary-card">
          <span class="label">Total Records</span>
          <span class="value">{{ reportItems.length }}</span>
        </div>
        <div class="summary-card" v-if="userType === 'student' && sessionLabel">
          <span class="label">Session</span>
          <span class="value">{{ sessionLabel }}</span>
        </div>
      </div>

      <div class="table-container">
        <table class="ledger-style-table">
          <thead>
            <tr v-if="userType === 'staff'">
              <th class="text-center" style="width: 5%">#</th>
              <th class="text-left" style="width: 28%">STAFF NAME</th>
              <th class="text-left" style="width: 24%">DESIGNATION</th>
              <th class="text-center" style="width: 22%">PHONE</th>
              <th class="text-center" style="width: 21%">STATUS</th>
            </tr>
            <tr v-else>
              <th class="text-center" style="width: 4%">#</th>
              <th class="text-center" style="width: 8%">GR. NO</th>
              <th class="text-left" style="width: 15%">STUDENT NAME</th>
              <th class="text-left" style="width: 15%">FATHER'S NAME</th>
              <th class="text-center" style="width: 10%">CLASS / SEC</th>
              <th class="text-center" style="width: 13%">STUDENT</th>
              <th class="text-center" style="width: 13%">FATHER</th>
              <th class="text-center" style="width: 12%">MOTHER</th>
              <th class="text-center" style="width: 10%">STATUS</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="userType === 'staff'">
              <tr v-for="(item, index) in reportItems" :key="'staff-' + index">
                <td class="text-center font-weight-bold">{{ index + 1 }}</td>
                <td class="text-left font-weight-bold">
                  {{ (item.name || "-").toUpperCase() }}
                </td>
                <td class="text-left">
                  {{ (item.designation || "-").toUpperCase() }}
                </td>
                <td class="text-center phone-cell">
                  {{ resolveStaffPhone(item).display }}
                </td>
                <td class="text-center">
                  <span
                    class="status-pill"
                    :class="status === 'active' ? 'is-active' : 'is-inactive'"
                  >
                    {{ status === "active" ? "INSTALLED" : "PENDING" }}
                  </span>
                </td>
              </tr>
            </template>
            <template v-else>
              <tr
                v-for="(item, index) in reportItems"
                :key="'student-' + index"
              >
                <td class="text-center font-weight-bold">{{ index + 1 }}</td>
                <td class="text-center gr-cell">{{ item.grNo || "-" }}</td>
                <td class="text-left font-weight-bold">
                  {{ (item.name || "-").toUpperCase() }}
                </td>
                <td class="text-left">
                  {{ (item.father_name || "-").toUpperCase() }}
                </td>
                <td class="text-center">
                  {{
                    [item.className, item.sectionName]
                      .filter(Boolean)
                      .join(" - ") || "-"
                  }}
                </td>
                <td
                  v-for="contact in getStudentContacts(item)"
                  :key="'contact-' + index + '-' + contact.key"
                  class="text-center contact-cell"
                  :class="
                    contact.installed
                      ? 'contact-cell--installed'
                      : 'contact-cell--missing'
                  "
                >
                  <span class="contact-cell__phone">{{
                    contact.phone || "-"
                  }}</span>
                  <span class="contact-cell__state">{{
                    contact.installed ? "✓" : "✕"
                  }}</span>
                </td>
                <td class="text-center">
                  <span
                    class="status-pill"
                    :class="status === 'active' ? 'is-active' : 'is-inactive'"
                  >
                    {{ status === "active" ? "INSTALLED" : "PENDING" }}
                  </span>
                </td>
              </tr>
            </template>
            <tr v-if="reportItems.length === 0">
              <td
                :colspan="userType === 'staff' ? 5 : 9"
                class="text-center py-5 no-data"
              >
                <feather-icon
                  icon="InboxIcon"
                  size="32"
                  class="text-muted mb-2"
                />
                <p>No records found for the selected criteria</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "AppStatsPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      cID: "",
      userType: "student",
      status: "active",
      sessionID: 0,
      sessionLabel: "",
    };
  },
  computed: {
    currentDateTime() {
      return new Date().toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    userTypeLabel() {
      return this.userType === "staff" ? "Staff" : "Student";
    },
    statusLabel() {
      return this.status === "active" ? "Active" : "Inactive";
    },
    reportTitle() {
      return `App Stats - ${this.userTypeLabel} (${this.statusLabel})`;
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = query.cID || this.$store.state.userData.cId || 0;
      this.userType = query.userType === "staff" ? "staff" : "student";
      this.status = query.status === "inactive" ? "inactive" : "active";
      this.sessionID = query.sessionID ? Number(query.sessionID) : 0;
      this.sessionLabel = query.sessionLabel || "";

      if (!this.db) {
        this.error =
          "Database identifier is missing. Please close and try again.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const response = await axios.get(
          "https://apiv26.myskool.app/api/campuses",
          {
            params: { db: this.db, cId: this.cID },
            headers: {
              Authorization: `Bearer ${this.$store.state.userData.token}`,
              accept: "application/json",
            },
          }
        );

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.campusName = match.name;
            this.campusBranch = this.cID == 0 ? "All Campuses" : match.branch;
            this.schoolLogo = match.logo;
          }
        }
      } catch (e) {
        const branches = this.$store.state.branches || [];
        const currentBranch =
          branches.find((b) => b.id == this.cID) ||
          this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.branch ||
            "MY SKOOL";
          this.campusBranch = currentBranch.branch || "";
        }
      }
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      try {
        let url =
          (this.$store.state.domain || "https://apiv26.myskool.app/api/") +
          "Users/GetAppDownloadStatistics?db=" +
          encodeURIComponent(this.db) +
          "&campusID=" +
          this.cID;

        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        const res = response.data?.data || response.data || {};
        const isStaff = this.userType === "staff";
        const isActive = this.status === "active";

        if (isStaff) {
          this.reportItems = isActive
            ? res.activeStaffList || []
            : res.inactiveStaffList || [];
        } else {
          this.reportItems = isActive
            ? res.activeStudentsList || []
            : res.inactiveStudentsList || [];
        }
      } catch (err) {
        console.error("Error fetching app stats report:", err);
        this.error = "Failed to retrieve app stats data.";
      } finally {
        this.loading = false;
      }
    },
    hasContact(value) {
      const text = value == null ? "" : String(value).trim();
      return text !== "" && text !== "-" && text.toLowerCase() !== "null";
    },
    getStudentContacts(item) {
      if (!item || typeof item !== "object") return [];
      const pickPhone = (keys) => {
        for (const key of keys) {
          if (this.hasContact(item[key])) return String(item[key]).trim();
        }
        return "";
      };
      return [
        {
          key: "student",
          phone: pickPhone([
            "phoneNumberStudent",
            "contact_student",
            "phoneNumber",
          ]),
          installed: this.hasContact(item.deviceID_Student),
        },
        {
          key: "father",
          phone: pickPhone(["phoneNumberFather", "contact_father"]),
          installed: this.hasContact(item.deviceID_Father),
        },
        {
          key: "mother",
          phone: pickPhone(["phoneNumberMother", "contact_mother"]),
          installed: this.hasContact(item.deviceID_Mother),
        },
      ];
    },
    resolveStaffPhone(item) {
      if (this.hasContact(item?.phoneNumber)) {
        const number = String(item.phoneNumber).trim();
        return { number, display: number };
      }
      if (this.hasContact(item?.contact_number)) {
        const number = String(item.contact_number).trim();
        return { number, display: number };
      }
      if (this.hasContact(item?.contact)) {
        const number = String(item.contact).trim();
        return { number, display: number };
      }
      return { number: "", display: "-" };
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.app-stats-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;
}

.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1200px;
  margin: 30px auto;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 130px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 2rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }
  }
}

.summary-section {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 1.5rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #eee;

  .summary-card {
    display: flex;
    flex-direction: column;
    align-items: center;

    .label {
      font-size: 0.8rem;
      font-weight: 700;
      color: #636e72;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-bottom: 4px;
    }

    .value {
      font-size: 1.2rem;
      font-weight: 800;
    }
  }
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  border: 1px solid #dee2e6;
  background: white;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 12px 15px;
    box-sizing: border-box;
    font-size: 0.95rem;
  }

  thead {
    background: #2c3e50 !important;

    th {
      color: white !important;
      font-weight: 800;
      text-transform: uppercase;
      font-size: 0.9rem;
      letter-spacing: 0.5px;
    }
  }

  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }

  .gr-cell {
    font-weight: 700;
    color: #6c5ce7;
  }

  .phone-cell {
    font-size: 0.88rem;
    font-weight: 700;
    line-height: 1.25;
    white-space: nowrap;
  }

  .contact-cell {
    font-size: 0.82rem;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;

    &--installed {
      color: #047857;
    }

    &--missing {
      color: #b91c1c;
    }

    &__phone {
      display: block;
    }

    &__state {
      display: inline-block;
      margin-top: 1px;
      font-size: 0.7rem;
      font-weight: 800;
      opacity: 0.9;
    }
  }
}

.status-pill {
  display: inline-block;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.03em;

  &.is-active {
    background: #d1fae5;
    color: #047857;
  }

  &.is-inactive {
    background: #fee2e2;
    color: #b91c1c;
  }
}

@media print {
  .no-print {
    display: none !important;
  }

  .app-stats-report {
    background: white !important;
    padding: 0 !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 1.2cm 0.8cm !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .ledger-style-table {
    font-size: 9pt !important;

    th,
    td {
      padding: 6px 10px !important;
      border: 1px solid #000 !important;
      color: #000 !important;
    }

    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .contact-cell--installed {
      color: #047857 !important;
    }

    .contact-cell--missing {
      color: #b91c1c !important;
    }
  }
}
</style>
